/**
 * Competencies React Query Hooks
 *
 * Custom hooks for competency management operations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { competenciesApi, CreateCompetencyInput, UpdateCompetencyInput } from '../../lib/competenciesApi';

// Query keys for competencies
export const competencyKeys = {
  all: ['competencies'] as const,
  lists: () => [...competencyKeys.all, 'list'] as const,
  list: (filters?: { type?: string }) => [...competencyKeys.lists(), filters] as const,
  detail: (id: number) => [...competencyKeys.all, 'detail', id] as const,
  basis: () => [...competencyKeys.all, 'basis'] as const,
  vak: () => [...competencyKeys.all, 'vak'] as const,
  forTask: (taskId: number) => [...competencyKeys.all, 'forTask', taskId] as const,
};

/**
 * Hook to fetch all competencies
 */
export function useCompetencies() {
  return useQuery({
    queryKey: competencyKeys.lists(),
    queryFn: () => competenciesApi.getAll(),
  });
}

/**
 * Hook to fetch a single competency by ID
 */
export function useCompetency(id: number | undefined) {
  return useQuery({
    queryKey: competencyKeys.detail(id!),
    queryFn: () => competenciesApi.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch basis competencies
 */
export function useBasisCompetencies() {
  return useQuery({
    queryKey: competencyKeys.basis(),
    queryFn: () => competenciesApi.getBasis(),
  });
}

/**
 * Hook to fetch vak (task-specific) competencies
 */
export function useVakCompetencies() {
  return useQuery({
    queryKey: competencyKeys.vak(),
    queryFn: () => competenciesApi.getVak(),
  });
}

/**
 * Hook to fetch competencies linked to a specific task
 */
export function useCompetenciesForTask(taskId: number) {
  return useQuery({
    queryKey: competencyKeys.forTask(taskId),
    queryFn: () => competenciesApi.getForTask(taskId),
    enabled: !!taskId,
  });
}

/**
 * Hook to create a competency
 */
export function useCreateCompetency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCompetencyInput) => competenciesApi.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: competencyKeys.all });
    },
  });
}

/**
 * Hook to update a competency
 */
export function useUpdateCompetency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: number; input: UpdateCompetencyInput }) =>
      competenciesApi.update(id, input),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: competencyKeys.all });
      queryClient.invalidateQueries({ queryKey: competencyKeys.detail(id) });
    },
  });
}

/**
 * Hook to delete a competency
 */
export function useDeleteCompetency() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => competenciesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: competencyKeys.all });
    },
  });
}
